import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { LANGUAGES, useTranslation } from "../lib/i18n";

export const LanguageSwitcher = ({ compact = false, className = "" }) => {
  const { lang, setLang, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  const active = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("nav.language")}
        className={`inline-flex items-center gap-2 border border-white/10 hover:border-gold/60 text-white/80 hover:text-gold transition-colors duration-300 ${
          compact ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-xs"
        } uppercase tracking-[0.18em]`}
      >
        <Globe className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        <span>{active.short}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-2 min-w-[180px] z-50 glass-strong py-2"
        >
          {LANGUAGES.map((l) => {
            const isActive = l.code === lang;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => {
                    setLang(l.code);
                    setOpen(false);
                  }}
                  data-testid={`language-option-${l.code}`}
                  className={`w-full flex items-center justify-between gap-4 px-4 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "text-gold"
                      : "text-white/75 hover:text-gold hover:bg-white/5"
                  }`}
                >
                  <span>
                    <span className="text-[10px] uppercase tracking-[0.24em] text-white/45 mr-2">
                      {l.short}
                    </span>
                    {l.label}
                  </span>
                  {isActive && <Check className="h-3.5 w-3.5" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
